<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { Bol, RouteStopDto } from "@/api/trips/dto/trip.dto";
import RouteStopIcon from "@/components/icons/RouteStopIcon.vue";
import { getStopShortInfo } from "@/utils/stopInfoTitle";

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    bol: Bol;
    routeStops: RouteStopDto[];
    allocatedBolIds?: string[];
  }>(),
  {
    allocatedBolIds: () => [],
  },
);

const relatedPickUp = computed(() => {
  return props.routeStops.find((routeStop) => routeStop.id === props.bol.routeStopId);
});

const isAllocated = computed<boolean>(() => {
  return props.allocatedBolIds.includes(props.bol.id);
});

const statusLabel = computed<string>(() => {
  return isAllocated.value ? t("general.collected") : t("general.not-allocated");
});

const pickUpInfo = computed<string>(() => {
  if (!relatedPickUp.value) {
    return "";
  }

  return getStopShortInfo(relatedPickUp.value);
});
</script>

<template>
  <div
    :class="{
      'border-primary': isAllocated,
    }"
    class="bg-grey-lighten-4 d-flex flex-column pa-4 reload-bol-card rounded-lg row-gap-3"
  >
    <div class="d-flex align-center col-gap-2 justify-space-between">
      <span class="font-size-14 font-weight-500 text-grey-navy" data-qa="reload-bol-number">
        {{ t("trip.bol-card-number", { number: bol.number }) }}
      </span>

      <div v-if="isAllocated" class="bg-indigo-lighten-5 px-2 rounded d-flex align-center col-gap-1">
        <v-icon size="xsmall">mdi-check</v-icon>
        <span class="font-size-12">{{ statusLabel }}</span>
      </div>

      <span v-else class="font-size-12 text-grey-darken-3">
        {{ statusLabel }}
      </span>
    </div>

    <div v-if="relatedPickUp" class="d-flex align-center col-gap-1" data-qa="reload-bol-pick-up">
      <route-stop-icon :type="relatedPickUp.type" size="20" />
      <span class="font-size-14 text-grey-darken-1">{{ pickUpInfo }}</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.reload-bol-card {
  min-width: 250px;
  width: max-content;
}
</style>
